import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useCycle } from '@/lib/cycle';
import { PageHeader, Card, KPI, Loading, Empty, SectionBadge } from '@/components/UI';
import { formatDateTime, formatCurrency } from '@/lib/format';
import { Upload } from 'lucide-react';

export default function NPD() {
  const { currentCycle, currentUnit } = useCycle();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [parsedAt, setParsedAt] = useState(null);

  useEffect(() => {
    if (!currentCycle || !currentUnit) return;
    setLoading(true);
    api.get(`/data/npd-pipeline/${currentCycle.year}/${currentCycle.month}?unit=${currentUnit.code}`).then((r) => {
      setData(r.data?.data || null); setParsedAt(r.data?.parsed_at || null);
    }).finally(() => setLoading(false));
  }, [currentCycle, currentUnit]);

  if (!currentUnit || !currentCycle) return <Empty title="No unit/cycle selected" />;
  if (loading) return <Loading />;
  if (!data || !data.funnel) return <Empty title={`No NPD data for ${currentUnit.name}`} hint="Upload the NPD JSON in the Upload Center." action={<button className="btn-primary" onClick={() => nav('/uploads')}><Upload size={14} className="inline mr-1" /> Go to Upload Center</button>} />;

  const totalFunnelValue = (data.funnel || []).reduce((s, f) => s + (f.value || 0), 0);
  return (
    <div className="space-y-6">
      <PageHeader title={`NPD Pipeline — ${currentUnit.name}`} subtitle={`Last sync: ${formatDateTime(parsedAt)} • ${data.record_counts?.rfqs || 0} RFQs, ${data.record_counts?.costings || 0} costings`} sourceBadge={`NPD JSON (${currentUnit.code}) — Uploaded`} actions={<button className="btn-ghost text-sm" onClick={() => nav('/uploads')}>Re-upload</button>} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Pipeline Value" value={formatCurrency(data.pipeline_value)} status="navy" />
        <KPI label="Won Value" value={formatCurrency(data.won_value)} status="green" />
        <KPI label="Active RFQs" value={data.active_rfqs_count} status={data.active_rfqs_count > 0 ? 'amber' : 'green'} />
        <KPI label="Overdue Quotes" value={data.overdue_quotes} status={data.overdue_quotes > 0 ? 'red' : 'green'} />
      </div>
      <Card title="RFQ Funnel">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {(data.funnel || []).map((f, i) => {
            const pct = totalFunnelValue > 0 ? Math.round((f.value / totalFunnelValue) * 100) : 0;
            return (<div key={i} className="text-center p-3 border border-[var(--aampl-border)] rounded"><div className="text-[10px] uppercase tracking-wider text-slate-500">{f.stage}</div><div className="text-2xl font-bold aampl-navy-text mt-1">{f.count}</div><div className="text-xs text-slate-600">{formatCurrency(f.value)}</div><div className="h-1 bg-slate-100 rounded mt-2 overflow-hidden"><div className="h-full aampl-gold-bg" style={{width: `${pct}%`}}></div></div></div>);
          })}
        </div>
      </Card>
      <Card title="Active RFQs (by aging)">
        {(data.active_rfqs || []).length === 0 ? <div className="text-sm text-slate-500 py-4 text-center">No active RFQs.</div> : (
          <div className="aampl-table-wrap"><table className="aampl-table"><thead><tr><th>RFQ#</th><th>Customer</th><th>Model</th><th>Received</th><th>Due</th><th className="text-right">Parts</th><th className="text-right">Value</th><th>Status</th><th className="text-right">Aging</th></tr></thead><tbody>
            {data.active_rfqs.slice(0, 50).map((r, i) => <tr key={i}><td className="font-mono text-xs">{r.rfq_number}</td><td>{r.customer}</td><td>{r.model_name}</td><td>{r.received_date}</td><td className={r.is_overdue ? 'text-rose-700 font-bold' : ''}>{r.due_date}</td><td className="text-right">{r.parts_count}</td><td className="text-right">{formatCurrency(r.value)}</td><td><SectionBadge status={r.status} label={r.status} /></td><td className="text-right">{r.aging_days}d</td></tr>)}
          </tbody></table></div>
        )}
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Costings by Status">
          <div className="space-y-2">
            {Object.entries(data.costings_by_status || {}).map(([k, v]) => <div key={k} className="flex justify-between text-sm border-b border-[var(--aampl-border)] pb-1"><span className="uppercase">{k}</span><span className="font-bold">{v}</span></div>)}
          </div>
        </Card>
        <Card title="Won Business">
          {(data.won_business || []).length === 0 ? <div className="text-sm text-slate-500">No won deals.</div> : (
            <div className="aampl-table-wrap"><table className="aampl-table"><thead><tr><th>Customer</th><th>Model</th><th className="text-right">Value</th></tr></thead><tbody>
              {data.won_business.map((w, i) => <tr key={i}><td>{w.customer}</td><td>{w.model}</td><td className="text-right">{formatCurrency(w.value)}</td></tr>)}
            </tbody></table></div>
          )}
        </Card>
      </div>
    </div>
  );
}
